import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, CheckCircle2, XCircle, Star } from "lucide-react";

export default function ReferenceConfirm() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!token) { setError("Missing reference link."); return; }
    api.get(`/references/lookup/${token}`)
      .then((r) => setCtx(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "This reference link is invalid or expired."));
  }, [token]);

  const submit = async (decision) => {
    setBusy(true);
    setError("");
    try {
      await api.post("/references/decide", { token, decision, reliability_rating: rating || undefined, comment });
      setResult(decision);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not submit.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-6">
        <div className="inline-flex w-14 h-14 rounded-md bg-brand items-center justify-center mb-4">
          <ShieldCheck className="w-7 h-7 text-white" strokeWidth={2.4} />
        </div>
        <h1 className="text-3xl font-outfit font-bold text-white">Reference request</h1>
        <p className="text-white/60 text-sm mt-1 font-manrope">Your reply stays private to SecuSource admin — the candidate does not see the details.</p>
      </div>

      {error && !ctx && <div className="card-flat p-6 text-center text-red-600 font-manrope">{error}</div>}

      {ctx?.already_submitted && (
        <div className="card-flat p-6 text-center space-y-2">
          <CheckCircle2 className="w-8 h-8 text-gold-500 mx-auto" />
          <div className="font-outfit font-semibold text-white">You&apos;ve already responded — thank you.</div>
          <div className="text-sm text-white/60 font-manrope">Status: <span className="uppercase font-outfit font-bold">{ctx.status}</span></div>
        </div>
      )}

      {result && (
        <div className="card-flat p-6 text-center space-y-2">
          {result === "confirmed" ? <CheckCircle2 className="w-8 h-8 text-gold-500 mx-auto" /> : <XCircle className="w-8 h-8 text-red-500 mx-auto" />}
          <div className="font-outfit font-semibold text-white">Reference {result}. Thank you.</div>
          <div className="text-sm text-white/60 font-manrope">SecuSource admin has been notified.</div>
        </div>
      )}

      {ctx && !ctx.already_submitted && !result && (
        <div className="card-flat p-6 space-y-5" data-testid="reference-form">
          <div>
            <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold mb-2">Candidate</div>
            <div className="font-outfit font-bold text-white text-lg">{ctx.candidate_name} <span className="text-white/50 font-manrope text-sm">· {ctx.candidate_ss_id || "—"}</span></div>
            <div className="text-sm text-white/70 font-manrope">{(ctx.candidate_roles || []).join(", ") || "—"} · {ctx.candidate_county || "—"}</div>
          </div>
          <div className="border-t border-white/10 pt-4">
            <div className="text-xs uppercase tracking-widest text-white/60 font-outfit font-bold mb-2">They stated</div>
            <div className="text-sm text-white font-manrope">Worked at <b>{ctx.company_name}</b>{ctx.role_held ? <> as <b>{ctx.role_held}</b></> : null}{ctx.period ? <> ({ctx.period})</> : null}.</div>
            {ctx.context && <div className="text-sm text-white/60 font-manrope italic mt-2">&ldquo;{ctx.context}&rdquo;</div>}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Reliability rating (optional)</Label>
            <div className="flex gap-1 mt-2" data-testid="ref-rating">
              {[1,2,3,4,5].map(n => (
                <button type="button" key={n} onClick={() => setRating(n)} className="p-1" data-testid={`ref-star-${n}`}>
                  <Star className={`w-8 h-8 ${n <= rating ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                </button>
              ))}
            </div>
            <div className="text-xs text-white/60 font-manrope mt-1">Rate their reliability, timekeeping, conduct and integrity.</div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-brand font-outfit font-bold">Comment (optional, private to admin)</Label>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Anything SecuSource admin should know…" data-testid="ref-comment" />
          </div>

          {error && <div className="text-sm text-red-600 font-manrope">{error}</div>}

          <div className="flex flex-wrap gap-3 pt-2">
            <Button onClick={() => submit("confirmed")} disabled={busy} className="flex-1 bg-gold-500 hover:bg-gold-600 text-white py-6 font-manrope font-semibold" data-testid="ref-confirm">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Confirm reference
            </Button>
            <Button onClick={() => submit("declined")} disabled={busy} variant="outline" className="flex-1 border-red-500 text-red-600 hover:bg-red-50 py-6 font-manrope font-semibold" data-testid="ref-decline">
              <XCircle className="w-4 h-4 mr-2" /> Decline
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
